import { Link } from "react-router-dom";
import ScriptTrackMockup from "./ScriptTrackMockup";
import TrustBadges from "./TrustBadges";
import type { LandingPageContent } from "@shared/landingPages";

/**
 * DscsaHero — above-the-fold hero section for the DSCSA landing page.
 * Two-column grid: copy + CTA on the left, ScriptTrack mockup on the right.
 * Follows the Hormozi conversion framework: outcome headline → effort-collapse
 * subheadline → primary CTA → trust micro-badges.
 */
export default function DscsaHero({ onCtaClick, content }: { onCtaClick?: () => void; content: LandingPageContent }) {
  return (
    <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* ── Left column: copy + CTA ──────────────────────────────── */}
          <div className="flex flex-col gap-6">
            {/* The managed offer is the primary promise; the deadline is useful
                context but is not presented as an approval countdown. */}
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full w-fit">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" aria-hidden="true" />
              Free account + included starter software
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-tight">
              {content.headline}
            </h1>

            {/* Subheadline — collapses perceived effort */}
            <p className="text-white/80 text-lg leading-relaxed max-w-lg">
              {content.subheadline}
            </p>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <Link
                to={content.ctaDestination}
                onClick={onCtaClick}
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white font-bold text-base px-8 py-4 rounded-btn shadow-lg shadow-emerald-900/30 transition-colors duration-200 w-full sm:w-auto"
              >
                {content.ctaText}
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a
                href="#readiness-assessment"
                className="inline-flex items-center justify-center text-white/85 hover:text-white font-semibold text-sm px-2 py-3 underline underline-offset-4 transition-colors"
              >
                Not ready to register? Check your readiness
              </a>
            </div>

            {/* Trust micro-badges */}
            <TrustBadges variant="dark" className="justify-start" signals={content.trustSignals} />

            {/* Secondary reassurance line */}
            <p className="text-white/50 text-xs">
              {content.reassurance}
            </p>
          </div>

          {/* ── Right column: ScriptTrack mockup ─────────────────────── */}
          <div className="flex justify-center lg:justify-end">
            <ScriptTrackMockup />
          </div>

        </div>
      </div>
    </section>
  );
}
